import { useCallback, useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'

import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { replace } from 'lib/routing/appRouting'
import { deleteSearchParams } from 'lib/url/searchUrlUtils'
import useQueryParams from 'hooks/useQueryParams'
import { LUXURY_PLUS } from 'luxPlus/constants/base'
import useJoinLuxPlusFreePreview from 'luxPlus/hooks/useJoinLuxPlusFreePreview'
import useLuxPlusFreePreviewUtils from 'luxPlus/hooks/useLuxPlusFreePreviewUtils'
import { getIsLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'

function useLuxPlusFreePreviewAutoEnrol() {
  const dispatch = useAppDispatch()
  const location = useLocation()
  const queryParams = useQueryParams()
  const isLuxPlusEnabled = useAppSelector(getIsLuxPlusEnabled)
  const { shouldAutoEnrolFreePreview: canAutoEnrolFreePreview } = useLuxPlusFreePreviewUtils()
  const { onPendingJoinFreePreview } = useJoinLuxPlusFreePreview()
  const hasAutoEnrolFreePreviewParam = queryParams.get(LUXURY_PLUS.FREE_PREVIEW_AUTO_ENROLL_QUERY_PARAM) === 'true'
  const enrolRequestedRef = useRef(false)

  const removeAutoEnrolFreePreviewParam = useCallback(() => {
    const search = deleteSearchParams(location.search, LUXURY_PLUS.FREE_PREVIEW_AUTO_ENROLL_QUERY_PARAM)
    dispatch(replace({ search, hash: location.hash }))
  }, [dispatch, location.hash, location.search])

  useEffect(() => {
    if (!hasAutoEnrolFreePreviewParam) return

    if (!canAutoEnrolFreePreview || !isLuxPlusEnabled) {
      removeAutoEnrolFreePreviewParam()
      return
    }

    if (enrolRequestedRef.current) return

    enrolRequestedRef.current = true
    onPendingJoinFreePreview()
    removeAutoEnrolFreePreviewParam()
  }, [
    hasAutoEnrolFreePreviewParam,
    isLuxPlusEnabled,
    onPendingJoinFreePreview,
    removeAutoEnrolFreePreviewParam,
    canAutoEnrolFreePreview,
  ])
}

export default useLuxPlusFreePreviewAutoEnrol
